import { Injectable } from '@angular/core';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { getHours, getMinutes, getDay, getDate, getMonth } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { DEFAULT_TIMEZONE, getTimezoneLabel } from './timezone.constants';

/**
 * Service for timezone-aware date/time operations.
 *
 * All provider calendars and slots are displayed in the provider's configured timezone,
 * NOT the browser's local timezone. This ensures consistency across different client locations.
 *
 * Usage:
 * - Provider views: Use provider's timezone from ProviderStore
 * - Public views: Use timezone from API response (e.g., AggregatedSlotsResponseDto.timezone)
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {
  /**
   * Format a UTC date/string to a time string in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone (e.g., "Europe/Berlin")
   * @param formatStr Optional format string (default: 'HH:mm')
   * @returns Formatted time string (e.g., "14:30")
   */
  formatTime(date: Date | string, timezone: string, formatStr = 'HH:mm'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatInTimeZone(dateObj, timezone, formatStr);
  }

  /**
   * Format a UTC date/string to a date string in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone (e.g., "Europe/Berlin")
   * @param formatStr Optional format string (default: 'dd.MM.yyyy')
   * @param locale Optional locale ('de' or 'en')
   * @returns Formatted date string (e.g., "28.01.2026")
   */
  formatDate(date: Date | string, timezone: string, formatStr = 'dd.MM.yyyy', locale: 'de' | 'en' = 'de'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatInTimeZone(dateObj, timezone, formatStr, {
      locale: locale === 'de' ? de : enUS,
    });
  }

  /**
   * Format a UTC date/string to a date+time string in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone (e.g., "Europe/Berlin")
   * @param formatStr Optional format string (default: 'dd.MM.yyyy HH:mm')
   * @param locale Optional locale ('de' or 'en')
   * @returns Formatted datetime string (e.g., "28.01.2026 14:30")
   */
  formatDateTime(date: Date | string, timezone: string, formatStr = 'dd.MM.yyyy HH:mm', locale: 'de' | 'en' = 'de'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatInTimeZone(dateObj, timezone, formatStr, {
      locale: locale === 'de' ? de : enUS,
    });
  }

  /**
   * Get the hour (0-23) of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Hour in the specified timezone
   */
  getHours(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return getHours(zonedDate);
  }

  /**
   * Get the minutes (0-59) of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Minutes in the specified timezone
   */
  getMinutes(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return getMinutes(zonedDate);
  }

  /**
   * Get the day of week (0=Sunday, 6=Saturday) of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Day of week in the specified timezone
   */
  getDayOfWeek(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return getDay(zonedDate);
  }

  /**
   * Get the day of month (1-31) of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Day of month in the specified timezone
   */
  getDayOfMonth(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return getDate(zonedDate);
  }

  /**
   * Get the month (0-11) of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Month in the specified timezone (0-indexed)
   */
  getMonth(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return getMonth(zonedDate);
  }

  /**
   * Get the year of a UTC date in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Year in the specified timezone
   */
  getYear(date: Date | string, timezone: string): number {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    return zonedDate.getFullYear();
  }

  /**
   * Convert a UTC date to a "zoned" Date object representing local time in the timezone.
   * Note: The returned Date still uses browser's TZ internally, but represents the correct local time.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Date object with values representing local time in the specified timezone
   */
  toZonedDate(date: Date | string, timezone: string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return toZonedTime(dateObj, timezone);
  }

  /**
   * Convert a local date (in specified timezone) to UTC Date.
   * Use when user selects a time in their provider's timezone that needs to be stored as UTC.
   * @param localDate Date representing local time in the timezone
   * @param timezone IANA timezone of the local date
   * @returns UTC Date
   */
  toUtc(localDate: Date, timezone: string): Date {
    return fromZonedTime(localDate, timezone);
  }

  /**
   * Get a formatted date string suitable for YYYY-MM-DD in the specified timezone.
   * Useful for date grouping/comparison.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns Date string in format "YYYY-MM-DD"
   */
  toDateString(date: Date | string, timezone: string): string {
    return this.formatDate(date, timezone, 'yyyy-MM-dd');
  }

  /**
   * Get the current UTC offset string for a timezone (e.g., "+01:00" or "+02:00" for summer time).
   * @param timezone IANA timezone
   * @param date Optional date to check offset for (defaults to now)
   * @returns Offset string like "+01:00"
   */
  getTimezoneOffset(timezone: string, date: Date = new Date()): string {
    return formatInTimeZone(date, timezone, 'xxx');
  }

  /**
   * Get a user-friendly label for a timezone.
   * @param timezone IANA timezone
   * @param locale 'de' or 'en'
   * @returns Friendly label (e.g., "Deutschland (MEZ/MESZ)")
   */
  getTimezoneLabel(timezone: string, locale: 'de' | 'en' = 'de'): string {
    return getTimezoneLabel(timezone, locale);
  }

  /**
   * Get current time info in a specific timezone.
   * Useful for "current time" indicators on calendars.
   * @param timezone IANA timezone
   * @returns Object with hours, minutes, and formatted time
   */
  getCurrentTimeInTimezone(timezone: string): { hours: number; minutes: number; formatted: string } {
    const now = new Date();
    return {
      hours: this.getHours(now, timezone),
      minutes: this.getMinutes(now, timezone),
      formatted: this.formatTime(now, timezone),
    };
  }

  /**
   * Check if two UTC dates are on the same day in the specified timezone.
   * @param date1 First UTC Date or ISO string
   * @param date2 Second UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns true if both dates are on the same day in the timezone
   */
  isSameDay(date1: Date | string, date2: Date | string, timezone: string): boolean {
    return this.toDateString(date1, timezone) === this.toDateString(date2, timezone);
  }

  /**
   * Format for datetime-local input, but in the specified timezone.
   * @param date UTC Date or ISO string
   * @param timezone IANA timezone
   * @returns String in format "YYYY-MM-DDTHH:mm" in the specified timezone
   */
  formatForDateTimeLocal(date: Date | string, timezone: string): string {
    return this.formatDateTime(date, timezone, "yyyy-MM-dd'T'HH:mm");
  }

  /**
   * Get the default timezone.
   */
  getDefaultTimezone(): string {
    return DEFAULT_TIMEZONE;
  }
}
